import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCalendarDays, LuClipboardList, LuClock, LuInfo, LuUser } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import { useTranslation } from '../../../i18n/LanguageContext';
import { BLOCK_TITLE, EmptyState, ProgressBar } from '../../ProfilePage/components/ProfileUi';
import { initialsOf, rateTone } from './personnelUtils';

const ROW = 'tw-flex tw-items-start tw-gap-3 tw-rounded-2xl tw-border tw-border-l-4 tw-border-slate-200/80 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]';

/** Date tile: day number over the short month. */
const DateTile = ({ value, locale }) => {
  const date = new Date(value);
  return (
    <span className="tw-flex tw-w-12 tw-shrink-0 tw-flex-col tw-items-center tw-rounded-xl tw-bg-slate-500/[0.08] tw-py-1.5 dark:tw-bg-white/[0.06]">
      <span className="tw-text-lg tw-font-semibold tw-leading-none tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{Number.isNaN(date.getTime()) ? '–' : date.getDate()}</span>
      <span className="tw-mt-0.5 tw-text-[10px] tw-font-semibold tw-uppercase tw-text-slate-500 dark:tw-text-slate-400">
        {Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(locale, { month: 'short' })}
      </span>
    </span>
  );
};

DateTile.propTypes = { value: PropTypes.any, locale: PropTypes.string };

/** One employee's period in Personel Raporları: totals, visits and jobs. */
const EmployeeStatsDialog = ({ isOpen, stats, range, getProjectName, onClose }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`operations.personnel.${key}`, vars);
  const lastStats = useRef(stats);
  if (stats) lastStats.current = stats;
  const shown = stats || lastStats.current;

  const tiles = shown ? [
    { key: 'totalVisits', value: shown.totalVisits, tone: 'tw-text-slate-900 dark:tw-text-white' },
    { key: 'completedVisits', value: shown.completedVisits, tone: 'tw-text-emerald-600 dark:tw-text-emerald-300' },
    { key: 'totalJobs', value: shown.totalJobs, tone: 'tw-text-sky-600 dark:tw-text-sky-300' },
    { key: 'completedJobs', value: shown.completedJobs, tone: 'tw-text-teal-600 dark:tw-text-teal-300' },
    { key: 'uniqueProjects', value: shown.projectsWorkedOn, tone: 'tw-text-violet-600 dark:tw-text-violet-300' },
  ] : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={shown?.employeeName || ''}
      subtitle={range}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <span aria-hidden="true" className="tw-grid tw-h-10 tw-w-10 tw-place-items-center tw-rounded-2xl tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-teal-500/30">
          {initialsOf(shown?.employeeName)}
        </span>
      )}
      footer={<Button onClick={onClose}>{label('close')}</Button>}
    >
      {shown && (
        <div className="tw-space-y-5">
          <dl className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-3 lg:tw-grid-cols-6">
            {tiles.map((tile) => (
              <div key={tile.key} className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <dd className={`tw-text-xl tw-font-semibold tw-leading-none tw-tabular-nums ${tile.tone}`}>{tile.value}</dd>
                <dt className="tw-mt-1 tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label(`summary.${tile.key}`)}</dt>
              </div>
            ))}
            <div className="tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
              <dd className="tw-flex tw-items-center tw-gap-2">
                <ProgressBar percent={shown.completionRate} tone={rateTone(shown.completionRate)} className="tw-flex-1" />
                <span className="tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{shown.completionRate}%</span>
              </dd>
              <dt className="tw-mt-1.5 tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label('successRate')}</dt>
            </div>
          </dl>

          <section className="tw-space-y-2.5">
            <h3 className={BLOCK_TITLE}>
              <LuClipboardList aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {label('visitDetails', { count: shown.visits.length })}
            </h3>
            {shown.visits.length === 0 ? (
              <EmptyState compact icon={LuCalendarDays} title={label('noVisits')} />
            ) : (
              <ul className="tw-grid tw-gap-2 lg:tw-grid-cols-2">
                {shown.visits.map((visit) => (
                  <li key={visit.id} className={`${ROW} ${visit.status ? 'tw-border-l-emerald-500' : 'tw-border-l-amber-400'}`}>
                    <DateTile value={visit.date || visit.firstVisitDate} locale={locale} />
                    <div className="tw-min-w-0 tw-flex-1 tw-space-y-1">
                      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                        <p className="tw-min-w-0 tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{getProjectName(visit.projectId) || label('noProject')}</p>
                        <Badge tone={visit.status ? 'success' : 'warning'}>{visit.status ? label('done') : label('ongoing')}</Badge>
                      </div>
                      <p className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                        <span className="tw-inline-flex tw-items-center tw-gap-1"><LuCalendarDays aria-hidden="true" className="tw-h-3 tw-w-3" />{label('days', { count: visit.period || 1 })}</span>
                        {visit.visitType && <span className="tw-inline-flex tw-items-center tw-gap-1"><LuInfo aria-hidden="true" className="tw-h-3 tw-w-3" />{visit.visitType}</span>}
                        {visit.patientId && <span className="tw-inline-flex tw-items-center tw-gap-1"><LuUser aria-hidden="true" className="tw-h-3 tw-w-3" />{visit.patientId}</span>}
                      </p>
                      {visit.info && <p className="tw-whitespace-pre-line tw-break-words tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">{visit.info}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="tw-space-y-2.5">
            <h3 className={BLOCK_TITLE}>
              <LuBriefcase aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {label('jobDetails', { count: shown.jobs?.length || 0 })}
            </h3>
            {(!shown.jobs || shown.jobs.length === 0) ? (
              <EmptyState compact icon={LuBriefcase} title={label('noJobs')} />
            ) : (
              <ul className="tw-grid tw-gap-2 lg:tw-grid-cols-2">
                {shown.jobs.map((job) => (
                  <li key={job.id} className={`${ROW} ${job.status ? 'tw-border-l-emerald-500' : 'tw-border-l-amber-400'}`}>
                    <DateTile value={job.startDate} locale={locale} />
                    <div className="tw-min-w-0 tw-flex-1 tw-space-y-1">
                      <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                        <p className="tw-min-w-0 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{job.title}</p>
                        <Badge tone={job.status ? 'success' : 'warning'}>{job.status ? label('done') : label('ongoing')}</Badge>
                      </div>
                      <p className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums">
                          <LuCalendarDays aria-hidden="true" className="tw-h-3 tw-w-3" />
                          {new Date(job.startDate).toLocaleDateString(locale)} – {new Date(job.endDate).toLocaleDateString(locale)}
                        </span>
                        {job.time && <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums"><LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />{job.time}</span>}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Modal>
  );
};

EmployeeStatsDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  stats: PropTypes.object,
  range: PropTypes.string.isRequired,
  getProjectName: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EmployeeStatsDialog;
